"use client";

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'sonner';
import { useRouter } from "next/navigation";
import { useSession, useUser } from '@clerk/nextjs';
import { DataGrid, DataGridColumnHeader, TDataGridRequestParams, useDataGrid } from '../common/data-grid';
import { ScreenLoader } from '../common/loaders/ScreenLoader';
import { fetchPackageBuilder, fetchStatisticsData, downloadPkg } from '@/lib/action';
import { KeenIcon } from '../common/keenicons';
import Link from 'next/link';
import { Toolbar } from '@mui/material';
import { ToolbarHeading } from '../common/toolbar';
import { useSelector } from 'react-redux';
import { PackageView } from './PackageView/PackageView';

const PackageList = ({ ...props }: any) => {
    const { formatMessage } = useIntl();
    const router = useRouter();
    const { user, isLoaded: userLoaded } = useUser();
    const { session, isLoaded: sessionLoaded } = useSession();
    const [searchQuery, setSearchQuery] = useState('');
    const intl = useIntl();
    const [details, setDetails] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const { permission, permissionLoading, membership, membershipLoading } = useSelector((state: any) => state.customer);
    const [statisticsData, setStatisticsData] = useState<any>(null);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const handleViewModalClose = () => {
        setViewModalOpen(false);
        setDetails(null)
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes] = await Promise.all([
                    fetchStatisticsData(user?.id, session?.id)
                ]);

                if (statsRes?.data?.error || statsRes?.error) {
                    toast.error(statsRes?.data?.error || statsRes?.error || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
                } else {
                    setStatisticsData(statsRes?.data);
                }
            } catch (err) {
                toast.error(intl.formatMessage({ id: 'FAILED_TO_FETCH_STATISTICS' }));
                console.error(err);
            }
        };

        if (user?.id && session?.id) {
            fetchData();
        }
    }, [user?.id, session?.id]);

    useEffect(() => {
        if (!permissionLoading && !membershipLoading && !isRedirecting && (permission?.packagebuilder == false)) {
            toast.error(intl.formatMessage({ id: 'NO_PERMISSION_VIEW_PAGE' }));
            setIsRedirecting(true);
            router.push('/');
        }
    }, [permissionLoading, permission, membershipLoading, membership]);

    useEffect(() => {
        if (userLoaded && !user?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
        }

        if (sessionLoaded && !session?.id) {
            router.push('/sign-in');
            toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
        }

    }, [userLoaded, sessionLoaded, user, session]);

    const fetchPackages = async (params: TDataGridRequestParams) => {
        try {
            if (!user?.id) {
                toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            if (!session?.id) {
                toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
                return {
                    data: [],
                    totalCount: 0
                };
            }

            const queryParams = new URLSearchParams();
            queryParams.set('searchJoin', 'or');
            queryParams.set('page', String(params.pageIndex + 1));
            queryParams.set('pageSize', String(params.pageSize));

            if (params.sorting?.[0]?.id) {
                queryParams.set('orderBy', params.sorting[0].id);
                queryParams.set('sortedBy', params.sorting[0].desc ? 'desc' : 'asc');
            }

            if (searchQuery.trim().length > 0) {
                queryParams.set('search', searchQuery);
            }
            const packageRes = await fetchPackageBuilder(user.id, session.id, queryParams.toString());


            if (packageRes?.data?.error || packageRes?.error) {
                toast.error(packageRes?.data?.error || packageRes?.error || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
            } else {
                return {
                    data: packageRes?.data?.data,
                    totalCount: packageRes?.data?.total ?? 5
                };
            }

        } catch (error) {
            toast(formatMessage({ id: 'FAILED_TO_FETCH_ORDER_LIST_DATA' }));

            return {
                data: [],
                totalCount: 0
            };
        }

    };

    const downloadPackage = async (id: any) => {
        try {
            const _res = await downloadPkg(id, user?.id, session?.id);
            const downloadBaseURL = process.env.NEXT_PUBLIC_DOWNLOAD_PACKAGE_URL;
            if (_res?.data?.message || _res?.message) {
                toast.error(_res?.data?.message || _res?.message || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
            }
            if (_res?.data?.error || _res?.error) {
                toast.error(_res?.data?.message || _res?.message || _res?.data?.err || _res?.err || _res?.data?.error || _res?.error || _res?.data?.msg || _res?.msg || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }))
            } else if (_res?.data?.file?.link) {
                const relativeLink = _res.data.file.link;
                const fullURL = `${downloadBaseURL}${relativeLink.startsWith('/') ? relativeLink.slice(1) : relativeLink}`;
                const link = document.createElement("a");
                link.href = fullURL;
                link.download = _res.data.file.name || "download";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err: any) {
            toast.error(err?.message || intl.formatMessage({ id: 'MEMBERSHIP.ERROR' }));
        }
    };

    const columns = useMemo<ColumnDef<any>[]>(
        () => [
            {
                accessorFn: (row) => row.index,
                id: 'index',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: 'ID', defaultMessage: 'ID' })}
                        column={column}
                    />
                ),
                enableSorting: false,
                cell: (info) => (
                    <span
                        className="leading-none font-medium text-sm text-gray-900"
                    >
                        {info.row.original.id}
                    </span>
                ),
                meta: {
                    headerTitle: formatMessage({ id: 'ID', defaultMessage: 'ID' })
                },

            },
            {
                accessorKey: 'name',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'PACKAGE_NAME', })} column={column} />,
                cell: (info) => (
                    <div className='text-body text-sm transition-colors'>
                        <span title={info.row.original.name} className="flex items-center gap-1">
                            {info.row.original.name}
                        </span>
                    </div>
                ),
                enableSorting: true,
                enableHiding: false,
                meta: {
                    headerTitle: formatMessage({ id: 'PACKAGE_NAME' }),
                }
            },
            {
                accessorFn: (row) => row.vendor,
                id: 'vendor',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "VENDOR" })}
                        column={column} />
                ),
                enableSorting: true,
                enableHiding: false,
                cell: (info) => (
                    <div className='text-body text-sm transition-colors'>
                        <span title={info.row.original.vendor} className="flex items-center gap-1">
                            {info.row.original.vendor}
                        </span>
                    </div>
                ),
                meta: {
                    headerTitle: formatMessage({ id: "VENDOR" }),
                }
            },
            {
                accessorFn: (row) => row.description,
                id: 'description',
                header: ({ column }) => (
                    <DataGridColumnHeader
                        title={formatMessage({ id: "DESCRIPTION" })}
                        column={column} />
                ),
                enableSorting: true,
                enableHiding: false,
                cell: (info) => (
                    <div className='text-body text-sm transition-colors'>
                        <span title={info.row.original.description} className="flex items-center gap-1">
                            {info.row.original.description}
                        </span>
                    </div>
                ),
                meta: {
                    headerTitle: formatMessage({ id: "DESCRIPTION" }),
                }
            },
            {
                accessorKey: 'typo3version',
                header: ({ column }) => <DataGridColumnHeader title={formatMessage({ id: 'TYPO3_VERSION', })} column={column} />,
                cell: (info) => (
                    <div className='text-body text-sm transition-colors'>
                        <span title={info.row.original.typo3version} className="flex items-center gap-1">
                            {info.row.original.typo3version}
                        </span>
                    </div>
                ),
                enableSorting: true,
                enableHiding: false,
                meta: {
                    headerTitle: formatMessage({ id: 'TYPO3_VERSION' }),
                }
            },
            {
                id: 'edit',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    const currentRow = info.row.original;
                    const membershipId = currentRow?.membership_id;
                    const matchedMembership = statisticsData?.memberships?.find((item: any) => {
                        return Number(item?.data?.id) === Number(membershipId);
                    });
                    const editEnabled = matchedMembership?.package_info?.edit === true;
                    const isDisabled = !editEnabled;
                    return (
                        <Link href={'/packagebuilder/packages/' + info.row.original.id}
                            className={`cursor-pointer btn btn-sm btn-icon btn-clear btn-light ${isDisabled ? 'disabled' : 'cursor-pointer'}`}
                        >
                            <KeenIcon icon="ki-filled ki-notepad-edit" />
                        </Link >
                    )
                },
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
            {
                id: 'download',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => {
                    const currentRow = info.row.original;
                    const membershipId = currentRow?.membership_id;
                    const matchedMembership = statisticsData?.memberships?.find((item: any) => {
                        return Number(item?.data?.id) === Number(membershipId);
                    });
                    const downloadEnabled = matchedMembership?.package_info?.download === true;
                    const isDisabled = !downloadEnabled;
                    return (
                        <button className={`cursor-pointer btn btn-sm btn-icon btn-clear btn-light ${isDisabled ? 'disabled' : 'cursor-pointer'}`}
                            onClick={() => downloadPackage(info.row.original.id)}
                            disabled={isDisabled}
                        >
                            <KeenIcon icon="exit-down" />
                        </button>
                    )
                },
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
            {
                id: 'view',
                header: () => '',
                enableSorting: false,
                enableHiding: false,
                cell: (info) => (
                    <span
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={async (e) => {
                            setDetails(info.row.original);
                            setViewModalOpen(true);
                        }}
                    >
                        <KeenIcon icon="eye" />
                    </span>
                ),
                meta: {
                    headerClassName: 'w-[60px]',
                },
            },
        ],
        [formatMessage, statisticsData]
    );


    if (permissionLoading || isRedirecting || membershipLoading) {
        return <ScreenLoader />;
    }

    if (!userLoaded && !sessionLoaded) {
        return (<ScreenLoader />)
    }

    if (user == undefined || user == null || session == undefined || session == null) {
        return (<ScreenLoader />)
    }

    const Listbar = ({ setSearchQuery }: any) => {
        const [inputValue, setInputValue] = useState(searchQuery);
        const { table } = useDataGrid();

        // const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        //     if (e.key === 'Enter') {
        //         setSearchQuery(inputValue);
        //         table.setPageIndex(0);
        //         if (inputValue.trim() === '') {
        //             table.setColumnFilters(
        //                 table.getState().columnFilters.filter((filter) => filter.id !== 'query')
        //             );
        //         } else {
        //             table.setColumnFilters([
        //                 ...table.getState().columnFilters.filter((filter) => filter.id !== 'query'),
        //                 { id: 'query', value: inputValue },
        //             ]);
        //         }
        //     }
        // };

        // const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        //     setInputValue(event.target.value);
        // };

        return (
            <div className="card-header bTag-b-0 px-5">
                {
                    props?.membership ?
                        <h3 className="card-title"><FormattedMessage id="PACKAGE_LIST" /></h3>
                        : <h3 className="card-title"> </h3>
                }
                {/* if serachbar needed just uncomment the code  */}
                {/* <div className="flex items-center flex-wrap gap-2.5">
                    <div className="input input-sm max-w-48">
                        <KeenIcon icon="magnifier" />
                        <input
                            type="text"
                            placeholder={formatMessage({ id: 'SEARCH.PACKAGES' })}
                            value={inputValue}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                </div> */}

            </div>
        );
    };

    return (
        <Fragment>
            <div className={`${props?.membership ? "" : "container-fixed"}`}>
                {
                    props?.membership ?
                        ""
                        :
                        <Toolbar className='flex justify-between !p-0'>
                            <ToolbarHeading title={<FormattedMessage id="PACKAGE_LIST" />} description="" />
                            <Link
                                href={'/packagebuilder/packages/'}
                                className={`${permission?.packagebuilder === false || permission?.package_info?.add == false && permission?.package_info?.current < permission?.package_info?.max
                                    ? 'disabled'
                                    : 'cursor-pointer'
                                    } btn btn-sm btn-primary`}
                            >
                                <i className="ki-filled ki-plus"></i> <FormattedMessage id="ADD_NEW_PACKAGE" />
                            </Link>
                        </Toolbar>
                }
            </div>
            <div className={`${props?.membership ? "" : "container-fixed"}`}>
                <DataGrid
                    columns={columns}
                    serverSide={true}
                    onFetchData={fetchPackages}
                    rowSelection={true}
                    getRowId={(row: any) => row.id}
                    pagination={{ size: props?.total ?? 10 }}
                    toolbar={<Listbar setSearchQuery={setSearchQuery} />}
                    layout={{
                        card: true,
                        classes: {
                            container: props?.total ? 'd-none-data-pagination' : ""
                        }
                    }}
                />
            </div>
            {viewModalOpen && <PackageView open={viewModalOpen} onOpenChange={handleViewModalClose} details={details} />}
        </Fragment>
    );
};

export default PackageList;







